import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { smart_meters_base_url } from '../../../../../../utils/urls';

const DashboardTab = () => {
  const [meterCounts, setMeterCounts] = useState({ openMeters: 0, closedMeters: 0, totalMeters: 0 });
  const [monthlyUsageData, setMonthlyUsageData] = useState({ labels: [], data: [], raw: [] });
  const [monthlyConsumption, setMonthlyConsumption] = useState(0);
  const [dailyConsumption, setDailyConsumption] = useState(0);
  const [currentDate, setCurrentDate] = useState('');
  const [currentYearMonth, setCurrentYearMonth] = useState('');
  
  // Financial data state
  const [financialData, setFinancialData] = useState({
    totalTransactions: 0,
    currentMonthTotal: 0,
    averageDaily: 0,
    averageTransaction: 0
  });
  
  // Updated state for bulk consumption - now including all bulk meter types
  const [bulkMetersData, setBulkMetersData] = useState({
    'City Council Bulk': 0,
    'Borehole Bulk': 0,
    'Bulk Inlet': 0,
    'Bulk Outlet': 0,
    'Bulk Borehole Outlet': 0
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [monthlyUsageLoading, setMonthlyUsageLoading] = useState(true);

  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  const fetchMonthlyUsageData = async (facilityId) => {
    setMonthlyUsageLoading(true);
    try {
      const response = await makeRequest2(
        `${smart_meters_base_url}/monthly_meter_graphs/${facilityId}`,
        'GET'
      );

      if (response?.data?.usageData) {
        const { labels, data, raw } = response.data.usageData;

        const chartData = labels.map((label, index) => ({
          meter_sn: label,
          usage: data[index] ? Number(data[index].toFixed(2)) : 0
        }));

        setMonthlyUsageData({
          labels,
          data,
          raw,
          chartData
        });
      } else {
        setMonthlyUsageData({ labels: [], data: [], raw: [], chartData: [] });
      }
    } catch (error) {
      toastify('Error fetching monthly usage data', 'error');
    } finally {
      setMonthlyUsageLoading(false);
    }
  };

  // Updated function to fetch bulk consumption data - now includes all bulk meter types
  const fetchBulkConsumptionData = async (facilityId) => {
    try {
      const bulkResponse = await makeRequest2(
        `${smart_meters_base_url}/bulk_meters_consumption/${facilityId}`,
        'GET'
      );
      
      // Check if response exists and has usageData
      if (bulkResponse?.data?.usageData) {
        const { raw } = bulkResponse.data.usageData;
        
        // Update bulk meters data with the response, keeping defaults for missing descriptions
        setBulkMetersData({
          'City Council Bulk': raw['City Council Bulk'] || 0,
          'Borehole Bulk': raw['Borehole Bulk'] || 0,
          'Bulk Inlet': raw['Bulk Inlet'] || 0,
          'Bulk Outlet': raw['Bulk Outlet'] || 0,
          'Bulk Borehole Outlet': raw['Bulk Borehole Outlet'] || 0
        });
      } else {
        // Silently reset to defaults if no usageData
        setBulkMetersData({
          'City Council Bulk': 0,
          'Borehole Bulk': 0,
          'Bulk Inlet': 0,
          'Bulk Outlet': 0,
          'Bulk Borehole Outlet': 0
        });
      }
    } catch (error) {
      // Completely silent - no console error, no toast
      // Just set defaults and continue - this handles facilities with no bulk meters
      setBulkMetersData({
        'City Council Bulk': 0,
        'Borehole Bulk': 0,
        'Bulk Inlet': 0,
        'Bulk Outlet': 0,
        'Bulk Borehole Outlet': 0
      });
    }
  };

  // Fetch financial/transaction data
  const fetchFinancialData = async (facilityId) => {
    try {
      // Fetch total transactions amount
      const totalResponse = await makeRequest2(
        `${smart_meters_base_url}/total_transactions_amount/${facilityId}`,
        'GET'
      );

      // Fetch current month total
      const monthlyResponse = await makeRequest2(
        `${smart_meters_base_url}/current_month_transactions/${facilityId}`,
        'GET'
      );

      setFinancialData({
        totalTransactions: totalResponse?.data?.totalAmount || 0,
        currentMonthTotal: monthlyResponse?.data?.monthlyAmount || 0,
        averageDaily: 0, // Placeholder for future implementation
        averageTransaction: 0 // Placeholder for future implementation
      });
    } catch (error) {
      // Silently handle error - just keep defaults at 0
      setFinancialData({
        totalTransactions: 0,
        currentMonthTotal: 0,
        averageDaily: 0,
        averageTransaction: 0
      });
    }
  };

  useEffect(() => {
    const fetchMeterData = async () => {
      if (!selectedFacilityId) return;
      setIsLoading(true);
      try {
        const countResponse = await makeRequest2(
          `${smart_meters_base_url}/stats/${selectedFacilityId}`,
          'GET'
        );
        if (countResponse?.data?.stats) {
          const { openMeters, closedMeters, totalMeters } = countResponse.data.stats;
          setMeterCounts({
            openMeters: openMeters || 0,
            closedMeters: closedMeters || 0,
            totalMeters: totalMeters || 0,
          });
        }

        // Fetch daily consumption data - updated for new format
        const dailyConsumptionResponse = await makeRequest2(
          `${smart_meters_base_url}/daily_meter_consumption/${selectedFacilityId}`,
          'GET'
        );

        if (dailyConsumptionResponse?.data) {
          // Handle the new simplified response format
          setDailyConsumption(dailyConsumptionResponse.data.totalConsumption || 0);
          setCurrentDate(dailyConsumptionResponse.data.date || '');
        } else {
          setDailyConsumption(0);
          setCurrentDate('');
        }

        // Fetch monthly consumption data - updated for new format
        const consumptionResponse = await makeRequest2(
          `${smart_meters_base_url}/monthly_meter_consumption/${selectedFacilityId}`,
          'GET'
        );

        if (consumptionResponse?.data) {
          setMonthlyConsumption(consumptionResponse.data.totalConsumption || 0);
          setCurrentYearMonth(consumptionResponse.data.yearMonth || '');
        } else {
          setMonthlyConsumption(0);
          setCurrentYearMonth('');
        }

        // Fetch bulk consumption data - now completely silent
        await fetchBulkConsumptionData(selectedFacilityId);
        await fetchMonthlyUsageData(selectedFacilityId);
        await fetchFinancialData(selectedFacilityId);

      } catch (error) {
        toastify('Error fetching meter data', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchMeterData();
  }, [selectedFacilityId]);

  const renderMonthlyUsageTooltip = (props) => {
    if (!props.active || !props.payload || !props.payload.length) return null;
    const { payload } = props;
    return (
      <div className="custom-tooltip bg-white p-2 border rounded shadow">
        <p className="mb-1">
          <strong>Meter: {payload[0].payload.meter_sn}</strong>
        </p>
        <p className="mb-0">
          Monthly Usage: <strong>{payload[0].value.toFixed(2)} m³</strong>
        </p>
      </div>
    );
  };

  const CustomXAxisTick = (props) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          transform="rotate(-90)"
          style={{ fontSize: '11px' }}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // Format the date for better display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        // Full date (YYYY-MM-DD)
        return `${parts[2]}/${parts[1]}/${parts[0]}`; // DD/MM/YYYY format
      } else if (parts.length === 2) {
        // Year-Month (YYYY-MM)
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthIndex = parseInt(parts[1], 10) - 1;
        return `${monthNames[monthIndex]} ${parts[0]}`; // MMM YYYY format
      }
      return dateString;
    } catch (e) {
      return dateString;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="tab-pane fade show active" role="tabpanel">
      {/* First Row - 5 cards including Monthly Consumption */}
      <div className="row g-3">
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-info">Total Meters</h6>
              <h3>{isLoading ? '...' : meterCounts.totalMeters}</h3>
              <p className="text-muted mb-0">Installed in facility</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-success">Open Meters</h6>
              <h3>{isLoading ? '...' : meterCounts.openMeters}</h3>
              <p className="text-muted mb-0">Available for readings</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-danger">Closed Meters</h6>
              <h3>{isLoading ? '...' : meterCounts.closedMeters}</h3>
              <p className="text-muted mb-0">Disconnected meters</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-danger">Daily Consumption</h6>
              <h3>{isLoading ? '...' : (dailyConsumption || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">{currentDate ? formatDate(currentDate) : 'Today'}</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-danger">Monthly Consumption</h6>
              <h3>{isLoading ? '...' : (monthlyConsumption || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">{currentYearMonth ? formatDate(currentYearMonth) : 'Current month'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Second Row - 5 bulk meter cards */}
      <div className="row g-3 mt-3">
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-success">City Council Bulk</h6>
              <h3>{isLoading ? '...' : (bulkMetersData['City Council Bulk'] || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Municipal supply</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-success">Borehole Bulk</h6>
              <h3>{isLoading ? '...' : (bulkMetersData['Borehole Bulk'] || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Groundwater source</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-warning">Bulk Inlet</h6>
              <h3>{isLoading ? '...' : (bulkMetersData['Bulk Inlet'] || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Main inlet supply</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-danger">Bulk Outlet</h6>
              <h3>{isLoading ? '...' : (bulkMetersData['Bulk Outlet'] || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Main outlet flow</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-danger">Bulk Borehole Outlet</h6>
              <h3>{isLoading ? '...' : (bulkMetersData['Bulk Borehole Outlet'] || 0).toFixed(2)} m³</h3>
              <p className="text-muted mb-0">Borehole outlet flow</p>
            </div>
          </div>
        </div>
      </div>

      {/* Third Row - 5 financial/transaction cards */}
      <div className="row g-3 mt-3">
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-primary">Total Transactions</h6>
              <h3>{isLoading ? '...' : formatCurrency(financialData.totalTransactions)}</h3>
              <p className="text-muted mb-0">All-time revenue</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-primary">Current Month</h6>
              <h3>{isLoading ? '...' : formatCurrency(financialData.currentMonthTotal)}</h3>
              <p className="text-muted mb-0">{currentYearMonth ? formatDate(currentYearMonth) : 'This month'}</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-secondary">Placeholder 1</h6>
              <h3>{formatCurrency(0)}</h3>
              <p className="text-muted mb-0">Coming soon</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-secondary">Placeholder 2</h6>
              <h3>{formatCurrency(0)}</h3>
              <p className="text-muted mb-0">Coming soon</p>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="card bg-light h-100">
            <div className="card-body">
              <h6 className="text-secondary">Placeholder 3</h6>
              <h3>{formatCurrency(0)}</h3>
              <p className="text-muted mb-0">Coming soon</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Meter Usage Chart from Utility Database */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Monthly Meter Usage (m³)</h5>
              <p className="text-muted">
                Monthly water usage for each meter
              </p>
              {monthlyUsageLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : monthlyUsageData.chartData && monthlyUsageData.chartData.length > 0 ? (
                <div style={{ width: '100%', height: 500 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={monthlyUsageData.chartData}
                      margin={{ top: 5, right: 30, left: 20, bottom: 100 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="meter_sn"
                        tick={<CustomXAxisTick />}
                        height={80}
                        interval={0}
                      />
                      <YAxis domain={[0, 'auto']} tickFormatter={(value) => value.toFixed(2)} />
                      <Tooltip content={renderMonthlyUsageTooltip} />
                      <Legend />
                      <Bar
                        dataKey="usage"
                        name="Monthly Usage (m³)"
                        fill="#4CAF50"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="alert alert-info">No monthly usage data available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;